/**
 * PromotionTooltip - Desktop tooltip for promotion callouts
 * Handles click-to-toggle tooltip with fixed positioning
 */
class PromotionTooltip extends HTMLElement {
  // Static reference to track currently open tooltip
  static currentlyOpen = null;

  constructor() {
    super();
    this.trigger = this.querySelector('.promotion-tooltip__trigger');
    this.content = this.querySelector('.promotion-tooltip__content');
    this.isOpen = false;
    this.movedToContainer = false;

    // Bind methods
    this.handleClick = this.handleClick.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleOutsideClick = this.handleOutsideClick.bind(this);
    this.handleEscapeKey = this.handleEscapeKey.bind(this);
    this.handleScroll = this.handleScroll.bind(this);
  }

  connectedCallback() {
    if (!this.trigger || !this.content) return;

    // Click to toggle tooltip
    this.trigger.addEventListener('click', this.handleClick);
    this.trigger.addEventListener('keydown', this.handleKeyDown);

    // Close on outside click
    document.addEventListener('click', this.handleOutsideClick);

    // Close on escape
    document.addEventListener('keydown', this.handleEscapeKey);

    // Reposition on scroll
    window.addEventListener('scroll', this.handleScroll, { passive: true });
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.handleOutsideClick);
    document.removeEventListener('keydown', this.handleEscapeKey);
    window.removeEventListener('scroll', this.handleScroll);
    
    // Remove tooltip from container if it was moved
    if (this.movedToContainer && this.content && this.content.parentNode) {
      this.content.parentNode.removeChild(this.content);
    }
    
    // Clean up static reference if this was the open tooltip
    if (PromotionTooltip.currentlyOpen === this) {
      PromotionTooltip.currentlyOpen = null;
    }
  }

  handleClick(event) {
    event.preventDefault();
    event.stopPropagation();
    this.toggle();
  }

  handleKeyDown(event) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.toggle();
    }
  }

  handleEscapeKey(event) {
    if (event.key === 'Escape' && this.isOpen) {
      this.close();
      this.trigger.focus();
    }
  }

  handleOutsideClick(event) {
    if (this.isOpen && !this.contains(event.target)) {
      this.close();
    }
  }

  handleScroll() {
    if (this.isOpen) {
      this.positionTooltip();
    }
  }

  positionTooltip() {
    const triggerRect = this.trigger.getBoundingClientRect();
    const tooltipWidth = this.content.offsetWidth;
    const tooltipHeight = this.content.offsetHeight;
    
    const gap = 12;
    const padding = 16;
    const arrowSize = 16;
    const arrowHalf = arrowSize / 2;

    // Get container offset (dialog or body)
    let containerOffsetX = 0;
    let containerOffsetY = 0;
    const parentDialog = this.content.closest('dialog');
    if (parentDialog) {
      const dialogRect = parentDialog.getBoundingClientRect();
      containerOffsetX = dialogRect.left;
      containerOffsetY = dialogRect.top;
    }

    const spaceAbove = triggerRect.top;
    const spaceBelow = window.innerHeight - triggerRect.bottom;

    const showAbove = spaceAbove > tooltipHeight + gap + padding || spaceAbove > spaceBelow;

    let top;
    if (showAbove) {
      top = triggerRect.top - tooltipHeight - gap - containerOffsetY;
      this.content.setAttribute('data-position', 'above');
    } else {
      top = triggerRect.bottom + gap - containerOffsetY;
      this.content.setAttribute('data-position', 'below');
    }

    // Position tooltip so arrow points to icon center
    const triggerCenter = triggerRect.left + triggerRect.width / 2;

    // Try to position tooltip with arrow at the right side pointing to trigger
    let left = triggerCenter - tooltipWidth + 94 - containerOffsetX; // Default: arrow near right side

    // Constrain to viewport (adjusted for container)
    const maxLeft = (parentDialog ? parentDialog.clientWidth : window.innerWidth) - tooltipWidth - padding;
    if (left < padding) {
      left = padding;
    }
    if (left > maxLeft) {
      left = maxLeft;
    }

    // Calculate arrow position relative to tooltip
    // Arrow should point to the center of the trigger
    const arrowLeft = (triggerCenter - containerOffsetX) - left - arrowHalf;

    // Clamp arrow position to stay within tooltip bounds (with some margin)
    const arrowMin = 20;
    const arrowMax = tooltipWidth - 20 - arrowSize;
    const clampedArrowLeft = Math.max(arrowMin, Math.min(arrowMax, arrowLeft));

    // Apply positions
    this.content.style.top = `${top}px`;
    this.content.style.left = `${left}px`;

    // Position arrow dynamically
    const arrow = this.content.querySelector('.promotion-tooltip__arrow');
    if (arrow) {
      arrow.style.left = `${clampedArrowLeft}px`;
      arrow.style.right = 'auto';
    }
  }

  open() {
    // Close any other open tooltip first
    if (PromotionTooltip.currentlyOpen && PromotionTooltip.currentlyOpen !== this) {
      PromotionTooltip.currentlyOpen.close();
    }

    // Move tooltip to appropriate container to avoid stacking context issues
    if (!this.movedToContainer) {
      // Check if we're inside a dialog (which uses top layer)
      const parentDialog = this.closest('dialog');
      if (parentDialog) {
        // Append to dialog to stay in same stacking context
        parentDialog.appendChild(this.content);
      } else {
        // Append to body to avoid transform parent issues
        document.body.appendChild(this.content);
      }
      this.movedToContainer = true;
    }
    
    this.content.hidden = false;
    this.positionTooltip();
    this.trigger.setAttribute('aria-expanded', 'true');
    this.isOpen = true;

    // Track this as the currently open tooltip
    PromotionTooltip.currentlyOpen = this;
  }

  close() {
    this.content.hidden = true;
    this.trigger.setAttribute('aria-expanded', 'false');
    this.isOpen = false;

    // Clear static reference if this was the open tooltip
    if (PromotionTooltip.currentlyOpen === this) {
      PromotionTooltip.currentlyOpen = null;
    }
  }

  toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }
}

if (!customElements.get('promotion-tooltip')) {
  customElements.define('promotion-tooltip', PromotionTooltip);
}

/**
 * PromotionExpandable - Handles expand/collapse for mobile tooltip content
 * Shows "Read More +" if content exceeds 5 lines (~105px)
 */
class PromotionExpandable extends HTMLElement {
  constructor() {
    super();
    this.content = this.querySelector('[data-content]');
    this.toggleButton = this.querySelector('[data-toggle]');

    // Line height × 5 lines = collapsed height
    this.collapsedHeight = 105; // 21px × 5 lines

    this.handleExpand = this.handleExpand.bind(this);
    this.checkOverflow = this.checkOverflow.bind(this);
  }

  connectedCallback() {
    if (!this.content || !this.toggleButton) return;

    this.toggleButton.addEventListener('click', this.handleExpand);

    // Check overflow when dialog opens
    const dialog = this.closest('dialog');
    if (dialog) {
      // Use MutationObserver to detect when dialog opens
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.attributeName === 'open' && dialog.open) {
            // Reset state when dialog opens
            this.reset();
            // Small delay to ensure content is rendered
            requestAnimationFrame(() => {
              this.checkOverflow();
            });
          }
        });
      });
      observer.observe(dialog, { attributes: true });
    }

    // Also check on window resize
    window.addEventListener('resize', this.checkOverflow);
  }

  disconnectedCallback() {
    if (this.toggleButton) {
      this.toggleButton.removeEventListener('click', this.handleExpand);
    }
    window.removeEventListener('resize', this.checkOverflow);
  }

  reset() {
    // Reset to collapsed state when dialog reopens
    this.content.setAttribute('data-collapsed', 'true');
    this.toggleButton.hidden = true;
  }

  checkOverflow() {
    if (!this.content) return;

    // Temporarily remove collapsed state to measure full height
    this.content.removeAttribute('data-collapsed');
    this.content.style.maxHeight = 'none';

    const scrollHeight = this.content.scrollHeight;

    // Remove inline style so CSS can take over
    this.content.style.maxHeight = '';

    // Check if content exceeds 5 lines
    if (scrollHeight > this.collapsedHeight) {
      // Content needs truncation - show button and collapse
      this.toggleButton.hidden = false;
      this.content.setAttribute('data-collapsed', 'true');
    } else {
      // Content fits - hide button, show all content
      this.toggleButton.hidden = true;
      this.content.removeAttribute('data-collapsed');
    }
  }

  handleExpand(event) {
    event.preventDefault();
    // Expand content and hide button
    this.content.setAttribute('data-collapsed', 'false');
    this.toggleButton.hidden = true;
  }
}

if (!customElements.get('promotion-expandable')) {
  customElements.define('promotion-expandable', PromotionExpandable);
}
